import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../../api/client';
import type { OfVault, SalesMediaItem } from '../../../api/client';

/**
 * The cross-vault catalogue: the union of live media across the vaults the user
 * has actually browsed. Fixed-ladder rungs pin bare `content_id`s that resolve
 * against this set, so the ladder editor needs it both to power the content
 * picker and to render thumbnails for already-pinned ids. There is no "attach as
 * sales pack" prerequisite — any vault's media can be pinned to a step directly.
 *
 * Media is fetched per-vault (there is no aggregate endpoint) and paged fully,
 * tagged with the owning vault. Unlike a full-account prefetch, a vault's media
 * is only ever requested via `loadVault` — i.e. when the user actually selects
 * that vault in the picker (or it's resolved as owning already-pinned content) —
 * since eagerly paging every vault in parallel on mount was hammering the
 * upstream vault-media proxy and causing 502s.
 */
export interface CatalogueItem extends SalesMediaItem {
  vault_id: string;
  vault_name: string;
}

export interface SalesCatalogue {
  items: CatalogueItem[];
  byId: Map<string, CatalogueItem>;
  vaults: { id: string; vault_name: string }[];
  /** True while the vault list or a requested vault's media is loading. */
  loading: boolean;
  error: string | null;
  /** Fetch a vault's media (paged to exhaustion) if it hasn't been loaded yet. */
  loadVault: (vaultId: string) => void;
  /** Re-read the vault list from the DB cache (no OF call). Runs on mount. */
  reload: () => Promise<void>;
  /** Re-sync the vault list from OnlyFans, then refresh — surfaces new vaults. */
  resync: () => Promise<void>;
}

const PAGE_SIZE = 48;

export function useSalesCatalogue(accountId: string): SalesCatalogue {
  const [items, setItems] = useState<CatalogueItem[]>([]);
  const [byId, setById] = useState<Map<string, CatalogueItem>>(new Map());
  const [vaults, setVaults] = useState<{ id: string; vault_name: string }[]>([]);
  const [vaultsLoading, setVaultsLoading] = useState(true);
  const [pendingVaultCount, setPendingVaultCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const runId = useRef(0);
  const vaultsRef = useRef<{ id: string; vault_name: string }[]>([]);
  const requestedVaults = useRef<Set<string>>(new Set());

  // Shared vault-list loader. `fetchVaults` is either the DB-cache read
  // (reload, run on mount) or the real OF re-sync (resync, the refresh button).
  // Both invalidate in-flight media paging and reset the browsed-media state.
  const loadVaults = useCallback(
    async (fetchVaults: () => Promise<OfVault[]>, failMsg: string) => {
      const myRun = ++runId.current;
      setVaultsLoading(true);
      setError(null);
      setItems([]);
      setById(new Map());
      requestedVaults.current = new Set();
      try {
        const accountVaults = await fetchVaults();
        if (myRun !== runId.current) return;
        const vaultList = accountVaults
          .map((v) => ({ id: v.id, vault_name: v.vault_name || v.id }))
          .sort((a, b) => a.vault_name.toLowerCase().localeCompare(b.vault_name.toLowerCase()));
        vaultsRef.current = vaultList;
        setVaults(vaultList);
      } catch (e) {
        if (myRun !== runId.current) return;
        setError(e instanceof Error ? e.message : failMsg);
      } finally {
        if (myRun === runId.current) setVaultsLoading(false);
      }
    },
    []
  );

  const reload = useCallback(
    () => loadVaults(() => api.getVaultsForAccount(accountId), 'Failed to load vault list'),
    [loadVaults, accountId]
  );

  const resync = useCallback(
    () => loadVaults(() => api.syncAccountVaults(accountId), 'Failed to sync vault list'),
    [loadVaults, accountId]
  );

  useEffect(() => {
    void reload();
    return () => {
      runId.current++; // invalidate any in-flight paging on unmount/account change
    };
  }, [reload]);

  // Fetch one vault's media, paged to exhaustion — only called when the caller
  // actually needs that vault (e.g. it's selected in the picker, or it owns
  // already-pinned content), and at most once per vault per mount.
  const loadVault = useCallback((vaultId: string) => {
    if (!vaultId || requestedVaults.current.has(vaultId)) return;
    const vault = vaultsRef.current.find((v) => v.id === vaultId);
    if (!vault) return;
    requestedVaults.current.add(vaultId);

    const myRun = runId.current;
    setPendingVaultCount((n) => n + 1);
    void (async () => {
      try {
        let offset = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const page = await api.fetchSalesVaultMedia(accountId, vaultId, offset, PAGE_SIZE);
          if (myRun !== runId.current) return;
          if (page.list.length) {
            const newItems = page.list.map((it) => ({ ...it, vault_id: vaultId, vault_name: vault.vault_name }));
            setItems((prev) => [...prev, ...newItems]);
            setById((prev) => {
              const m = new Map(prev);
              for (const ci of newItems) if (!m.has(ci.id)) m.set(ci.id, ci);
              return m;
            });
          }
          if (!page.has_more || page.list.length === 0) break;
          offset = page.next_offset;
        }
      } catch (e) {
        if (myRun !== runId.current) return;
        requestedVaults.current.delete(vaultId); // allow a retry
        setError(e instanceof Error ? e.message : 'Failed to load vault media');
      } finally {
        if (myRun === runId.current) setPendingVaultCount((n) => n - 1);
      }
    })();
  }, [accountId]);

  return { items, byId, vaults, loading: vaultsLoading || pendingVaultCount > 0, error, loadVault, reload, resync };
}
